import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { Bloom } from '../components/Bloom';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
const faq = [{
  question: 'How long does setup take?',
  answer: 'Most clients are fully live in about a week.'
}, {
  question: 'What do I have to do myself?',
  answer: 'A self-serve onboarding portal walks you through your login, the mobile app, syncing your calendar, a CRM tour, adding your team, and connecting your socials.'
}, {
  question: 'What does Viridia Analytics set up for me?',
  answer: 'On a scheduled call we build your AI receptionist in your brand voice, set up your business phone number and A2P registration, and build your workflows with you.'
}];
const selfServe = [{
  title: 'Get your login settled',
  body: 'Set your password and you are in.'
}, {
  title: 'Download the app',
  body: 'The whole system on your phone, not just the desk.'
}, {
  title: 'Sync your calendar',
  body: 'So booked estimates land where you already look.'
}, {
  title: 'Take the CRM tour',
  body: 'A quick walkthrough of where everything lives.'
}, {
  title: 'Add your team',
  body: 'Give your crew or office manager their own users.'
}, {
  title: 'Connect your socials',
  body: 'Link your accounts so posting and messages come through.'
}];
const withUs = [{
  title: 'Your AI receptionist',
  body: 'We build and tune it in your brand voice: how you answer, what you quote, what you never say.'
}, {
  title: 'Your business phone number',
  body: 'Set up so calls and texts run through the system instead of your personal cell.'
}, {
  title: 'A2P registration',
  body: 'The carrier paperwork that keeps your texts from getting filtered. We handle it.'
}, {
  title: 'Your workflows',
  body: 'Follow-ups, reminders, and review requests built with you on the call, for your services.'
}];
export function GettingStarted() {
  return <>
      <SEOHead title="Getting Started | Easy Onboarding + White-Glove Setup | Viridia Analytics" description="You handle the easy part in our onboarding portal; we build your AI receptionist, phone number, and workflows with you. Most clients live in about a week." canonical="/getting-started" faq={faq} data-id="element-2343" data-mp="1drdiukf88hnp" />

      <PageHeader label="Getting started" title="Getting started is the easy part." intro="You're not handed a login and left to figure it out. Setup splits in two: the simple stuff you knock out yourself in an onboarding portal, and the technical, in-your-voice stuff we build with you on a call." data-id="element-2344" data-mp="1sncxyf0r7i2c" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-2345" data-mp="26dy9finvopq7">
        <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-2346" data-mp="2bd1g17jib40a" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2347" data-mp="2lk3x7itaa4dv">
          <SectionLabel data-id="element-2348" data-mp="2gk9bdtod84zo">Your part</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2349" data-mp="2giepac1cy6u0">
            You handle the easy part.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-2350" data-mp="27k308eg53h3o">
            A portal walks you through it, one step at a time. Knock it out between jobs.
          </p>

          <ol className="mt-12 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2351" data-mp="2z9z327wu0and">
            {selfServe.map((s, i) => <motion.li key={s.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="border-t border-[#1c1c1c] pt-5" data-id="element-2352" data-mp="2ui7kajjx397s" data-mpi={i}>
                <p className="font-mono text-xs tracking-[0.2em] text-neutral-600" data-id="element-2353" data-mp="32rleqq0xe8qu">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="mt-3 text-base font-semibold text-white" data-id="element-2354" data-mp="37rpxynt7xhkr">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-2355" data-mp="2ts84hv4z9g52">{s.body}</p>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2356" data-mp="3lhjm13wwfxtf">
        <Bloom size="md" className="right-0 top-[-6rem]" data-id="element-2357" data-mp="3qhfr76hc1vak" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2358" data-mp="3qj0m4x7k7g8y">
          <SectionLabel data-id="element-2359" data-mp="3coq6d5djiwf1">Our part</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2360" data-mp="3gr3ajpcn2m9p">
            We handle the rest, with you.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-2361" data-mp="8a027321syug">
            The parts that have to sound like you get built on a call, with you on the line.
          </p>

          <ul className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] sm:grid-cols-2" data-id="element-2362" data-mp="1xquat61ciit9">
            {withUs.map(w => <li key={w.title} className="bg-[#0d0d0d] p-8" data-id="element-2363" data-mp="27ihxktmyjboe">
                <h3 className="text-base font-semibold text-white" data-id="element-2364" data-mp="22j58014pbqi4">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-2365" data-mp="22l87dbrquvzi">{w.body}</p>
              </li>)}
          </ul>

          <p className="mt-10 max-w-2xl text-lg leading-relaxed text-neutral-300" data-id="element-2366" data-mp="19uznnfhk83it">
            You do the easy part. We bring the brand, the voice, and the technical setup.
          </p>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2367" data-mp="1jse0l549ya6d">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-2368" data-mp="306dy69fndnqg" />
        <div className="mx-auto w-full max-w-3xl px-6 py-24 text-center lg:py-28" data-id="element-2369" data-mp="2v7syqc87z832">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2370" data-mp="2va5eciqi96cy">
            Most clients are fully live in about a week.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-2371" data-mp="378iulyo95x2b">
            Book the setup call and we will get the calendar sorted on the same call.
          </p>
          <div className="mt-9 flex justify-center" data-id="element-2372" data-mp="3h6yog9enzkfi">
            <CTAButton to="/contact" data-id="element-2373" data-mp="2jkj8xd5sswul">Book your setup call</CTAButton>
          </div>
          <p className="mt-6 flex items-center justify-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-neutral-600" data-id="element-2374" data-mp="2jioixkfo6ejn">
            Phoenix metro
            <ArrowRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-2375" data-mp="2thxbc8uvzl3w" />
            Month to month
          </p>
        </div>
      </section>
    </>;
}